import {
	Box,
	InputAdornment,
	ListItem,
	ListItemButton,
	ListItemIcon,
	MenuItem,
	Popper,
	Select,
	type SelectChangeEvent,
	SvgIcon,
	type SxProps,
	TextField,
	type Theme,
	Typography,
} from '@mui/material';
import Autocomplete, {
	type AutocompleteChangeReason,
	type AutocompleteRenderInputParams,
	type AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { borderRadius, palette, spaces } from '../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../utils/getMuiIcon';
import { searchInputSx } from '../../../utils/searchInputStyles';
import useScreenSize from '../../../utils/useScreenSize';
import { SearchCategory } from '../SearchBar';
import SearchEndAdornment from './SearchEndAdornment';

export const zIndex = {
	searchListItem: 1000,
	searchSuggestions: 3000,
} as const;

export enum SuggestionType {
	RecentSearch = 'recentSearch',
	SearchSuggestion = 'searchSuggestion',
}

export type Suggestion = {
	label: string;
	value: string;
	type: SuggestionType;
};

interface SearchBarInputBoxProps {
	suggestions: Suggestion[];
	placeholder: string;
	onSearch: (searchTerm: string, searchSuggestionApplied?: boolean) => void;
	inputValue: string;
	setInputValue: (value: string) => void;
	setCategory: (category: SearchCategory) => void;
	category: SearchCategory;
	disabled?: boolean;
}

interface InputBoxProps {
	renderInputParams: AutocompleteRenderInputParams;
	placeholder: string;
	onSearch: (searchTerm: string, searchSuggestionApplied?: boolean) => void;
	inputValue: string;
	setInputValue: (value: string) => void;
	setCategory: (category: SearchCategory) => void;
	category: SearchCategory;
}

interface AutoCompleteSuggestionProps {
	option: Suggestion;
	onSearch: (searchTerm: string, searchSuggestionApplied?: boolean) => void;
	setInputValue: (value: string) => void;
	inputValue: string;
	isDesktop: boolean;
	listItemProps: React.HTMLAttributes<HTMLLIElement>;
}

interface AutocompleteInputProps extends Pick<SearchBarInputBoxProps, 'disabled'> {
	suggestions: Suggestion[];
	inputValue: string;
	setInputValue: (value: string) => void;
	setCategory: (category: SearchCategory) => void;
	category: SearchCategory;
	placeholder: string;
	onSearch: (searchTerm: string, searchSuggestionApplied?: boolean) => void;
}

interface DesktopStartAdornmentProps {
	category: SearchCategory;
	setCategory: (category: SearchCategory) => void;
}

const DesktopStartAdornment = React.memo(
	({ category, setCategory }: DesktopStartAdornmentProps) => {
		const { t } = useTranslation();

		return (
			<InputAdornment position="start">
				<Select
					data-testid="search-bar-category-selector"
					value={category}
					onChange={(e: SelectChangeEvent) => setCategory(e.target.value as SearchCategory)}
					aria-label={t('Search category')}
					sx={{
						width: '160px!important',
						height: '40px',
						paddingLeft: `${spaces.md}!important`,
						paddingRight: `${spaces.md}!important`,
						backgroundColor: palette.common.white,
						borderRadius: borderRadius.full,
						border: `1px solid ${palette.elevation.outlined}`,
						cursor: 'pointer',
						'& .MuiSelect-select': {
							paddingX: '0px!important',
							paddingY: spaces.xs,
						},
						'&:hover': {
							borderColor: palette.secondary.main,
						},
						'&:focus, &.Mui-focused': {
							borderColor: palette.primary.main,
						},
						'& fieldset': {
							border: 'none!important',
						},
					}}
					inputProps={{
						'aria-label': t('Search category'),
					}}
					MenuProps={{
						sx: {
							marginTop: spaces.xxs,
							zIndex: `${zIndex.searchSuggestions}!important`,
							'& .MuiPaper-root': {
								elevation: 8,
								borderRadius: borderRadius.md,
								border: `1px solid ${palette.elevation.outlined}`,
								paddingX: spaces.xs,
							},
						},
						MenuListProps: {
							sx: {
								'& .MuiMenuItem-root': {
									borderRadius: borderRadius.sm,
									'&:hover, &:focus, &.Mui-selected': {
										backgroundColor: `${palette.primary.selected}!important`,
									},
								},
							},
						},
					}}
				>
					<MenuItem value={SearchCategory.Library}>{t('Library')}</MenuItem>
					<MenuItem value={SearchCategory.ProfessionalDevelopment}>
						{t('Professional Development')}
					</MenuItem>
				</Select>
			</InputAdornment>
		);
	}
);

const InputBox: React.FC<InputBoxProps> = ({
	renderInputParams,
	placeholder,
	onSearch,
	inputValue,
	setInputValue,
	setCategory,
	category,
}) => {
	const { t } = useTranslation();
	const { isXsScreen } = useScreenSize();

	const handleSearch = () => {
		onSearch(inputValue, false);
	};

	const MobileStartAdornment = () => (
		<InputAdornment position="start">
			<Select
				data-testid="search-bar-category-selector"
				value={category}
				onChange={(e: SelectChangeEvent) => setCategory(e.target.value as SearchCategory)}
				IconComponent={() => <></>}
				aria-label={t('Search category')}
				renderValue={() => (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							flexDirection: 'row',
							justifyContent: 'center',
						}}
					>
						<SvgIcon component={getMuiIcon('alleTab')} aria-hidden="true" />
						<SvgIcon component={getMuiIcon('arrowDropDown')} aria-hidden="true" />
					</Box>
				)}
				sx={{
					height: '40px',
					paddingLeft: `${spaces.sm}!important`,
					paddingRight: `${spaces.xxs}!important`,
					width: '64px!important',
					backgroundColor: palette.common.white,
					borderRadius: borderRadius.full,
					border: `1px solid ${palette.elevation.outlined}`,
					cursor: 'pointer',
					'& .MuiSelect-select': {
						paddingX: '0px!important',
						paddingY: spaces.xs,
					},
					'&:hover': {
						borderColor: palette.secondary.main,
					},
					'&:focus, &.Mui-focused': {
						borderColor: palette.primary.main,
					},
					'& fieldset': {
						border: 'none!important',
					},
				}}
				inputProps={{
					'aria-label': t('Search category'),
				}}
				MenuProps={{
					sx: {
						marginTop: spaces.xxs,
						zIndex: `${zIndex.searchSuggestions}!important`,
						'& .MuiPaper-root': {
							elevation: 8,
							borderRadius: borderRadius.md,
							border: `1px solid ${palette.elevation.outlined}`,
							paddingX: spaces.xs,
						},
					},
					MenuListProps: {
						sx: {
							'& .MuiMenuItem-root': {
								borderRadius: borderRadius.sm,
								'&:hover, &:focus, &.Mui-selected': {
									backgroundColor: `${palette.primary.selected}!important`,
								},
							},
						},
					},
				}}
			>
				<MenuItem value={SearchCategory.Library}>{t('Library')}</MenuItem>
				<MenuItem value={SearchCategory.ProfessionalDevelopment}>
					{t('Professional Development')}
				</MenuItem>
			</Select>
		</InputAdornment>
	);

	const searchInputId = renderInputParams.inputProps?.id || 'search-input';
	// Only use suggestionsId in mobile view where the Box with this id exists
	// In desktop, MUI Autocomplete handles its own listbox and ARIA attributes
	const suggestionsId = isXsScreen ? 'search-suggestions' : undefined;

	// In desktop, let Autocomplete handle aria-controls and aria-expanded via renderInputParams
	// In mobile, we need to explicitly set them to reference our custom Box element
	const ariaControls = isXsScreen ? suggestionsId : renderInputParams.inputProps?.['aria-controls'];
	const ariaExpanded = renderInputParams.inputProps?.['aria-expanded'] || false;
	const ariaDescribedBy = isXsScreen
		? suggestionsId
		: renderInputParams.inputProps?.['aria-describedby'];

	const defaultOnKeyDown = renderInputParams.inputProps?.onKeyDown;
	const activeDescendant = renderInputParams.inputProps?.['aria-activedescendant'];

	return (
		<TextField
			data-testid="search-bar-input-box__desktop-start-adornment"
			autoFocus={isXsScreen}
			{...renderInputParams}
			id={searchInputId}
			size="small"
			onChange={(e) => {
				setInputValue(e.target.value);
			}}
			onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
				defaultOnKeyDown?.(e);
				if (e.defaultPrevented) return;
				if (e.key === 'Enter' && !activeDescendant) {
					e.preventDefault();
					handleSearch();
				}
			}}
			inputProps={{
				...renderInputParams.inputProps,
				'data-testid': isXsScreen ? 'search-bar-input-mobile' : 'search-bar-input-desktop',
				'aria-label': t('Search'),
				'aria-describedby': ariaDescribedBy,
				'aria-autocomplete': 'list',
				'aria-expanded': ariaExpanded,
				'aria-controls': ariaControls,
			}}
			InputProps={{
				...renderInputParams.InputProps,
				placeholder,
				endAdornment: (
					<SearchEndAdornment
						value={inputValue}
						onClear={() => setInputValue('')}
						onSearch={handleSearch}
					/>
				),
				startAdornment: isXsScreen ? (
					<MobileStartAdornment />
				) : (
					<DesktopStartAdornment category={category} setCategory={setCategory} />
				),
				sx: {
					...(searchInputSx as SxProps<Theme>),
					paddingLeft: '0px!important',
				},
			}}
		/>
	);
};

export const AutoCompleteSuggestion = ({
	option,
	onSearch,
	isDesktop,
	inputValue,
	setInputValue,
	listItemProps,
}: AutoCompleteSuggestionProps) => {
	const matches = match(option.label, inputValue, { insideWords: true });
	const parts = parse(option.label, matches);
	const isRecentSearch = option.type === SuggestionType.RecentSearch;
	return (
		<ListItem
			{...listItemProps}
			key={option.value}
			value={option.value}
			sx={{
				zIndex: `${zIndex.searchListItem}!important`,
				padding: '0px!important',
			}}
		>
			<ListItemButton
				onClick={() => {
					if (isDesktop) return;
					setInputValue(option.value);
					onSearch(option.value, option.type === SuggestionType.SearchSuggestion);
				}}
				role="option"
				aria-selected={false}
				sx={{
					paddingX: spaces.md,
					paddingY: spaces.sm,
					borderRadius: borderRadius.sm,
					'&:hover': {
						backgroundColor: isDesktop ? palette.action.hover : palette.primary.selected,
					},
				}}
			>
				<ListItemIcon
					sx={{
						minWidth: '36px',
						padding: 0,
						margin: 0,
						color: palette.text.secondary,
					}}
				>
					{isRecentSearch ? (
						<SvgIcon
							data-testid="recent-search-icon"
							component={getMuiIcon('clock')}
							sx={{
								fill: 'transparent',
							}}
						/>
					) : (
						<SvgIcon
							data-testid="search-suggestion-icon"
							component={getMuiIcon('search')}
							viewBox="0 0 17 17"
							sx={{
								width: '24px',
								height: '24px',
								color: palette.text.secondary,
							}}
						/>
					)}
				</ListItemIcon>
				<Box data-testid="search-bar-suggestion">
					{parts.map((part, index) => (
						<Typography
							// biome-ignore lint/suspicious/noArrayIndexKey: index keys are pre-existing; switching the key source changes reconciliation behavior
							key={index}
							sx={{
								fontWeight: part.highlight || inputValue === '' ? 400 : 600, // Highlight the unmatched part
								color: palette.text.primary,
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
							component="span"
							data-testid={
								part.highlight || inputValue === ''
									? 'search-bar-suggestion-matched'
									: 'search-bar-suggestion-unmatched'
							}
						>
							{part.text}
						</Typography>
					))}
				</Box>
			</ListItemButton>
		</ListItem>
	);
};

const SearchBarInputBox: React.FC<SearchBarInputBoxProps> = ({
	inputValue,
	setInputValue,
	suggestions,
	placeholder,
	onSearch,
	category,
	setCategory,
	disabled,
}) => {
	const { isXsScreen } = useScreenSize();
	const { t } = useTranslation();

	return isXsScreen ? (
		<Box
			sx={{
				flex: 1,
				display: 'flex',
				flexDirection: 'column',
				overflowY: 'hidden',
			}}
		>
			<AutocompleteInputMobile
				suggestions={suggestions}
				inputValue={inputValue}
				category={category}
				setCategory={setCategory}
				setInputValue={setInputValue}
				placeholder={placeholder}
				onSearch={onSearch}
				disabled={disabled}
			/>
			<Box
				sx={{ paddingTop: spaces.xs, flex: 1, overflowY: 'auto' }}
				data-testid="suggestions-container"
				id="search-suggestions"
				role="listbox"
				aria-label={String(t('Search suggestions'))}
			>
				{suggestions.map((suggestion) => (
					<AutoCompleteSuggestion
						key={suggestion.value}
						option={suggestion}
						onSearch={onSearch}
						inputValue={inputValue}
						setInputValue={setInputValue}
						isDesktop={false}
						listItemProps={{
							onClick: () => {},
						}}
					/>
				))}
			</Box>
		</Box>
	) : (
		<AutocompleteInputDesktop
			suggestions={suggestions}
			inputValue={inputValue}
			setInputValue={setInputValue}
			category={category}
			setCategory={setCategory}
			placeholder={placeholder}
			onSearch={onSearch}
			disabled={disabled}
		/>
	);
};

export default SearchBarInputBox;

const getCommonAutocompleteProps = ({
	suggestions,
	inputValue,
	setInputValue,
	placeholder,
	onSearch,
	category,
	setCategory,
	disabled,
}: AutocompleteInputProps) => ({
	options: suggestions,
	freeSolo: true,
	disabled,
	inputValue,
	onInputChange: (_event: any, newInputValue: string) => {
		setInputValue(newInputValue);
	},
	onChange: (_event: any, _newValue: any, reason: AutocompleteChangeReason) => {
		if (reason === 'selectOption') {
			onSearch(
				typeof _newValue === 'string' ? _newValue : _newValue?.value || '',
				_newValue?.type === SuggestionType.SearchSuggestion
			);
		}
	},
	filterOptions: (options: Suggestion[]) => options,
	renderOption: (
		props: React.HTMLAttributes<HTMLLIElement>,
		option: Suggestion,
		{ inputValue }: { inputValue: AutocompleteRenderOptionState['inputValue'] }
	) => (
		<AutoCompleteSuggestion
			key={option.value}
			option={option}
			onSearch={onSearch}
			inputValue={inputValue}
			setInputValue={setInputValue}
			isDesktop={true}
			listItemProps={props}
		/>
	),
	renderInput: (params: AutocompleteRenderInputParams) => (
		<InputBox
			renderInputParams={params}
			placeholder={placeholder}
			onSearch={onSearch}
			inputValue={inputValue}
			setInputValue={setInputValue}
			category={category}
			setCategory={setCategory}
		/>
	),
});

const AutocompleteInputMobile: React.FC<AutocompleteInputProps> = ({
	suggestions,
	inputValue,
	setInputValue,
	placeholder,
	onSearch,
	category,
	setCategory,
	disabled,
}) => (
	<Autocomplete
		{...getCommonAutocompleteProps({
			suggestions,
			inputValue,
			setInputValue,
			placeholder,
			onSearch,
			category,
			setCategory,
			disabled,
		})}
		data-testid="search-bar-input-box-mobile"
		open={false}
	/>
);

const DesktopPopper = (props: any) => {
	return (
		<Popper
			{...props}
			sx={{
				'& .MuiAutocomplete-paper': {
					marginTop: spaces.xs,
					elevation: 8,
					borderRadius: borderRadius.md,
					border: `1px solid ${palette.elevation.outlined}`,
					padding: spaces.xs,
					paddingY: '0px',
					'& .MuiAutocomplete-listbox': {
						'::-webkit-scrollbar': {
							display: 'none',
						},
						scrollbarWidth: 'none', // Firefox
						msOverflowStyle: 'none', // IE/Edge
					},
				},
			}}
		/>
	);
};

const AutocompleteInputDesktop: React.FC<AutocompleteInputProps> = ({
	suggestions,
	inputValue,
	setInputValue,
	placeholder,
	onSearch,
	category,
	setCategory,
	disabled,
}) => (
	<Autocomplete
		data-testid="search-bar-input-box-desktop"
		PopperComponent={(props) => <DesktopPopper {...props} />}
		{...getCommonAutocompleteProps({
			suggestions,
			inputValue,
			setInputValue,
			placeholder,
			onSearch,
			category,
			setCategory,
			disabled,
		})}
	/>
);
